import React, { useState, useEffect, useRef } from "react";
import apiClient from "../../../api/apiClient";
import Loading from "../../../components/Loading/Loading";
import { useToast } from "../../../context/toastContext";
import { useConfirm } from "../../../context/confirmContext";

const PARTNER_TYPES = ["lab", "pos", "other"];
const PARTNER_INDUSTRIES = ["marijuana", "hemp", "hemp/marijuana"];
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const PartnerManagement = ({ userProfile }) => {
    const toast = useToast();
    const confirm = useConfirm();
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedPartners, setExpandedPartners] = useState(new Set());
    const [partnerKeys, setPartnerKeys] = useState({}); // partnerId -> keys[]
    const [keysLoading, setKeysLoading] = useState(new Set());
    const tableRef = useRef(null);

    // Create partner form 
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [createForm, setCreateForm] = useState({ name: "", type: "lab", contact_email: "", industry: "", state: "" });
    const [createSubmitting, setCreateSubmitting] = useState(false);
    const [createMsg, setCreateMsg] = useState({ type: "", text: "" });

    // Generate key modal
    const [genKeyModal, setGenKeyModal] = useState(null); // { partnerId, partnerName }
    const [genKeyForm, setGenKeyForm] = useState({ name: "", is_test: false });
    const [genKeySubmitting, setGenKeySubmitting] = useState(false);
    const [genKeyMsg, setGenKeyMsg] = useState({ type: "", text: "" });

    // Key reveal modal
    const [revealedKey, setRevealedKey] = useState(null); // { key: string }
    const [copied, setCopied] = useState(false);

    const fetchPartners = async () => {
        setLoading(true);
        try {
            const res = await apiClient.get("/admin/partners");
            setPartners(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchPartners(); }, []);

    const toggleExpand = async (partnerId) => {
        const isCurrentlyExpanded = expandedPartners.has(partnerId);

        setExpandedPartners((prev) => {
            const next = new Set(prev);
            next.has(partnerId) ? next.delete(partnerId) : next.add(partnerId);
            return next;
        });

        // Lazy-load keys when expanding if not already loaded
        if (!isCurrentlyExpanded && !partnerKeys[partnerId]) {
            setKeysLoading((prev) => new Set(prev).add(partnerId));
            try {
                const res = await apiClient.get(`/admin/partners/${partnerId}/keys`);
                setPartnerKeys((prev) => ({ ...prev, [partnerId]: res.data }));
            } catch (err) {
                console.error(err);
                setPartnerKeys((prev) => ({ ...prev, [partnerId]: [] }));
            } finally {
                setKeysLoading((prev) => {
                    const next = new Set(prev);
                    next.delete(partnerId);
                    return next;
                });
            }
        }
    };

    const handleCreatePartner = async (e) => {
        e.preventDefault();
        setCreateMsg({ type: "", text: "" });
        setCreateSubmitting(true);
        try {
            await apiClient.post("/admin/partners", createForm);
            setCreateMsg({ type: "success", text: "Partner created successfully." });
            setCreateForm({ name: "", type: "lab", contact_email: "", industry: "", state: "" });
            fetchPartners();
        } catch (err) {
            setCreateMsg({ type: "error", text: err.response?.data?.error || "Failed to create partner" });
        } finally {
            setCreateSubmitting(false);
        }
    };

    const openGenKeyModal = (partner) => {
        setGenKeyModal({ partnerId: partner.id, partnerName: partner.name });
        setGenKeyForm({ name: "", is_test: false });
        setGenKeyMsg({ type: "", text: "" });
    };

    const handleGenerateKey = async (e) => {
        e.preventDefault();
        setGenKeyMsg({ type: "", text: "" });
        setGenKeySubmitting(true);
        try {
            const res = await apiClient.post(`/admin/partners/${genKeyModal.partnerId}/keys`, genKeyForm);
            const partnerId = genKeyModal.partnerId;
            setGenKeyModal(null);
            setRevealedKey({ key: res.data.apiKey });
            setCopied(false);
            // Clear cached keys so they reload on next expand
            setPartnerKeys((prev) => ({ ...prev, [partnerId]: undefined }));
        } catch (err) {
            setGenKeyMsg({ type: "error", text: err.response?.data?.error || "Failed to generate key" });
        } finally {
            setGenKeySubmitting(false);
        }
    };

    const handleRevokeKey = async (keyId, partnerId) => {
        const ok = await confirm({
            title: "Revoke API Key",
            message: "Revoke this API key? This cannot be undone.",
            confirmLabel: "Revoke",
            variant: "danger",
        });
        if (!ok) return;
        try {
            await apiClient.delete(`/admin/keys/${keyId}`);
            setPartnerKeys((prev) => ({
                ...prev,
                [partnerId]: prev[partnerId]?.map((k) =>
                    k.id === keyId ? { ...k, is_active: false } : k
                ),
            }));
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to revoke key");
        }
    };

    const handleRotateKey = async (keyId, partnerId) => {
        const ok = await confirm({
            title: "Rotate API Key",
            message: "Rotate this key? The old key will be deactivated and a new key will be issued.",
            confirmLabel: "Rotate",
            variant: "warning",
        });
        if (!ok) return;
        try {
            const res = await apiClient.post(`/admin/keys/${keyId}/rotate`);
            setRevealedKey({ key: res.data.apiKey });
            setCopied(false);
            // Reload keys for this partner
            const keysRes = await apiClient.get(`/admin/partners/${partnerId}/keys`);
            setPartnerKeys((prev) => ({ ...prev, [partnerId]: keysRes.data }));
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to rotate key");
        }
    };

    const handleCopyKey = () => {
        if (!revealedKey?.key) return;
        navigator.clipboard.writeText(revealedKey.key).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const formatDate = (dt) => dt ? new Date(dt).toLocaleDateString() : "—";

    const PARTNER_HEADERS = ["ID", "Name", "Type", "Industry", "State", "Contact Email", "Status", "Created", "Actions"];

    return (
        <div>
            {/* Toolbar */}
            <div className="admin-search-bar">
                <span className="admin-search-count">{partners.length} partner{partners.length !== 1 ? "s" : ""}</span>
                {userProfile === "superadmin" && (
                    <button className="admin-action-btn superadmin-only" onClick={() => setShowCreateForm((v) => !v)}>
                        + Create Partner
                    </button>
                )}
            </div>

            {/* Create Partner inline form */}
            {showCreateForm && (
                <div style={{ padding: "var(--space-5)", borderBottom: "1px solid var(--color-border)", background: "var(--color-bg-secondary)" }}>
                    <h3 className="admin-create-title">
                        Create Integration Partner <span className="admin-role-tag superadmin">Superadmin</span>
                    </h3>
                    <form onSubmit={handleCreatePartner}>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label className="admin-form-label">Partner Name</label>
                                <input
                                    className="admin-form-input"
                                    required
                                    value={createForm.name}
                                    onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Type</label>
                                <select
                                    className="admin-form-input"
                                    value={createForm.type}
                                    onChange={(e) => setCreateForm((p) => ({ ...p, type: e.target.value }))}
                                >
                                    {PARTNER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Contact Email</label>
                                <input
                                    className="admin-form-input"
                                    type="email"
                                    value={createForm.contact_email}
                                    onChange={(e) => setCreateForm((p) => ({ ...p, contact_email: e.target.value }))}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Industry</label>
                                <select
                                    className="admin-form-input"
                                    value={createForm.industry}
                                    onChange={(e) => setCreateForm((p) => ({ ...p, industry: e.target.value }))}
                                >
                                    <option value="">— None —</option>
                                    {PARTNER_INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                                </select>
                            </div>
                            <div className="admin-form-group">
                                <label className="admin-form-label">State</label>
                                <select
                                    className="admin-form-input"
                                    value={createForm.state}
                                    onChange={(e) => setCreateForm((p) => ({ ...p, state: e.target.value }))}
                                >
                                    <option value="">— None —</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                        {createMsg.text && (
                            <p className={createMsg.type === "error" ? "admin-form-error" : "admin-form-success"}>{createMsg.text}</p>
                        )}
                        <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                            <button type="submit" className="admin-action-btn primary" disabled={createSubmitting}>
                                {createSubmitting ? "Creating…" : "Create Partner"}
                            </button>
                            <button
                                type="button"
                                className="admin-action-btn"
                                style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }}
                                onClick={() => { setShowCreateForm(false); setCreateMsg({ type: "", text: "" }); }}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Partners table */}
            {loading ? (
                <div className="admin-loading-wrapper"><Loading /></div>
            ) : (
                <div className="admin-table-scroll" ref={tableRef}>
                    <table className="admin-table">
                        <thead>
                            <tr>
                                {PARTNER_HEADERS.map((label) => (
                                    <th key={label}>{label}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {partners.length === 0 && (
                                <tr><td colSpan={PARTNER_HEADERS.length} className="admin-table-empty">No partners found.</td></tr>
                            )}
                            {partners.map((partner) => {
                                const isExpanded = expandedPartners.has(partner.id);
                                const keys = partnerKeys[partner.id];
                                const isLoadingKeys = keysLoading.has(partner.id);

                                return (
                                    <React.Fragment key={partner.id}>
                                        <tr>
                                            <td className="admin-td-secondary">{partner.id}</td>
                                            <td className="admin-td-name">
                                                <span className="admin-collapse-toggle" onClick={() => toggleExpand(partner.id)}>
                                                    {isExpanded ? "▼" : "▶"}
                                                </span>
                                                {partner.name}
                                            </td>
                                            <td className="admin-td-secondary">
                                                <span className="admin-role-tag">{partner.type}</span>
                                            </td>
                                            <td className="admin-td-secondary">{partner.industry || "—"}</td>
                                            <td className="admin-td-secondary">{partner.state || "—"}</td>
                                            <td className="admin-td-secondary">{partner.contact_email || "—"}</td>
                                            <td className={partner.is_active !== false ? "admin-status-active" : "admin-status-disabled"}>
                                                {partner.is_active !== false ? "Active" : "Inactive"}
                                            </td>
                                            <td className="admin-td-secondary">{formatDate(partner.created_at)}</td>
                                            <td className="admin-td-action">
                                                {userProfile === "superadmin" && (
                                                    <button
                                                        className="admin-action-btn primary"
                                                        onClick={() => openGenKeyModal(partner)}
                                                    >
                                                        Generate Key
                                                    </button>
                                                )}
                                            </td>
                                        </tr>

                                        {/* Keys sub-table */}
                                        {isExpanded && (
                                            <tr className="admin-detail-row">
                                                <td colSpan={PARTNER_HEADERS.length} className="admin-detail-cell">
                                                    {isLoadingKeys ? (
                                                        <div style={{ padding: "var(--space-3)" }}><Loading /></div>
                                                    ) : !keys || keys.length === 0 ? (
                                                        <p style={{ padding: "var(--space-3)", color: "var(--color-text-secondary)", margin: 0 }}>
                                                            No API keys for this partner.
                                                        </p>
                                                    ) : (
                                                        <div style={{ padding: "var(--space-3)" }}>
                                                            <table className="admin-table" style={{ margin: 0, fontSize: "var(--text-xs)" }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Key ID</th>
                                                                        <th>Name</th>
                                                                        <th>Test?</th>
                                                                        <th>Active</th>
                                                                        <th>Expires</th>
                                                                        <th>Last Used</th>
                                                                        <th>Created</th>
                                                                        <th>Actions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {keys.map((k) => (
                                                                        <tr key={k.key_id}>
                                                                            <td className="admin-td-secondary">{k.key_id}</td>
                                                                            <td>{k.name || "—"}</td>
                                                                            <td>{k.is_test ? "Yes" : "No"}</td>
                                                                            <td className={k.is_active ? "admin-status-active" : "admin-status-disabled"}>
                                                                                {k.is_active ? "Active" : "Revoked"}
                                                                            </td>
                                                                            <td className="admin-td-secondary">{formatDate(k.expires_at)}</td>
                                                                            <td className="admin-td-secondary">{formatDate(k.last_used_at)}</td>
                                                                            <td className="admin-td-secondary">{formatDate(k.created_at)}</td>
                                                                            <td className="admin-td-action">
                                                                                {k.is_active && userProfile === "superadmin" && (
                                                                                    <div style={{ display: "flex", gap: "var(--space-2)" }}>
                                                                                        <button
                                                                                            className="admin-action-btn disable"
                                                                                            onClick={() => handleRevokeKey(k.id, partner.id)}
                                                                                        >
                                                                                            Revoke
                                                                                        </button>
                                                                                        <button
                                                                                            className="admin-action-btn primary"
                                                                                            onClick={() => handleRotateKey(k.id, partner.id)}
                                                                                        >
                                                                                            Rotate
                                                                                        </button>
                                                                                    </div>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Generate Key modal */}
            {genKeyModal && (
                <div className="admin-modal-overlay" onClick={() => setGenKeyModal(null)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="admin-create-title">Generate API Key — {genKeyModal.partnerName}</h3>
                        <form onSubmit={handleGenerateKey}>
                            <div className="admin-form-group">
                                <label className="admin-form-label">Key Name</label>
                                <input
                                    className="admin-form-input"
                                    placeholder="e.g. Production Key"
                                    value={genKeyForm.name}
                                    onChange={(e) => setGenKeyForm((p) => ({ ...p, name: e.target.value }))}
                                />
                            </div>
                            <div className="admin-form-group" style={{ flexDirection: "row", alignItems: "center", gap: "var(--space-3)", marginTop: "var(--space-3)" }}>
                                <label className="admin-form-label" style={{ marginBottom: 0 }}>Test Key?</label>
                                <input
                                    type="checkbox"
                                    checked={genKeyForm.is_test}
                                    onChange={(e) => setGenKeyForm((p) => ({ ...p, is_test: e.target.checked }))}
                                />
                            </div>
                            {genKeyMsg.text && (
                                <p className={genKeyMsg.type === "error" ? "admin-form-error" : "admin-form-success"}>{genKeyMsg.text}</p>
                            )}
                            <div style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-4)" }}>
                                <button type="submit" className="admin-action-btn primary" disabled={genKeySubmitting}>
                                    {genKeySubmitting ? "Generating…" : "Generate Key"}
                                </button>
                                <button
                                    type="button"
                                    className="admin-action-btn"
                                    style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }}
                                    onClick={() => setGenKeyModal(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Key reveal modal */}
            {revealedKey && (
                <div className="admin-modal-overlay" onClick={() => setRevealedKey(null)}>
                    <div className="admin-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
                        <h3 className="admin-create-title">New API Key Generated</h3>
                        <div className="admin-key-warning">
                            Store this key securely — it will not be shown again.
                        </div>
                        <div className="admin-key-display">
                            {revealedKey.key}
                        </div>
                        <div style={{ display: "flex", gap: "var(--space-3)" }}>
                            <button className="admin-action-btn primary" onClick={handleCopyKey}>
                                {copied ? "Copied!" : "Copy Key"}
                            </button>
                            <button
                                className="admin-action-btn"
                                style={{ background: "var(--color-border)", color: "var(--color-text-primary)" }}
                                onClick={() => setRevealedKey(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PartnerManagement;
